// bd-deepcontext-zgbz — the URL-pool estimate panel, the one novel molecule of
// the Scheduled Jobs lock: a canvas inset (not a tinted alert box) whose STATUS
// LINE carries the colour — safe check / warn insufficient / risk empty — over
// muted breakdown lines, with an outline Apply pill and a warn coverage line.
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { formatCompactNumber } from "../../utils/formatCompactNumber";
import { grouped } from "./numberFormat";
import { recommendUrlsPerRun } from "../scheduledJobRecommendation";
import type { UrlPoolEstimate } from "./types";

// The panel copy is English-only, so number shapes are pinned to en-US rather
// than the visitor's locale (a hr-HR browser must not render "8,4 tis.").
const compact = (value: number) => formatCompactNumber(value, "en-US");

interface PoolEstimatePanelProps {
  promptCount: number;
  urlsPerRun: number;
  runsPerDay: number;
  poolEstimate: UrlPoolEstimate | null;
  estimateLoading: boolean;
  onApplyRecommendation: (urlsPerRun: number) => void;
}

const PoolEstimatePanel: React.FC<PoolEstimatePanelProps> = ({
  promptCount,
  urlsPerRun,
  runsPerDay,
  poolEstimate,
  estimateLoading,
  onApplyRecommendation,
}) => {
  if (promptCount === 0) return null;

  if (estimateLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-canvas px-3 py-2.5 text-xs text-accent-active">
        <ArrowPathIcon className="h-4 w-4 animate-spin" />
        Estimating URL pool...
      </div>
    );
  }

  if (!poolEstimate) return null;

  const isInsufficient = poolEstimate.total < urlsPerRun;
  const isEmpty = poolEstimate.total === 0;
  // bd-deepcontext-4x5yg — daily iterations mirror calculateJobDailyStats.
  // Unified job model (deepcontext-ia8xo.1): every job runs `runsPerDay` times.
  // runsPerDay can be NaN when the input is blank; recommendUrlsPerRun returns
  // null for that (and for an empty/unknown pool).
  const recommendedUrls = recommendUrlsPerRun(poolEstimate.total, runsPerDay);
  const countryEntries = Object.entries(poolEstimate.byCountry);
  const deadCountries = poolEstimate.coverage?.deadCountries ?? [];
  const hasNoCoverage =
    poolEstimate.coverage?.status === "no_domains_in_country" ||
    poolEstimate.coverageStatus === "no_domains_in_country";
  const hasMarketContext = poolEstimate.marketTotal > poolEstimate.total;
  const hasRequests =
    poolEstimate.totalRequests > 0 || poolEstimate.marketTotalRequests > 0;

  const statusTone = isEmpty
    ? "text-risk"
    : isInsufficient
      ? "text-warn"
      : "text-safe";

  return (
    <div
      data-testid="pool-estimate-panel"
      className="flex flex-col gap-1.5 rounded-xl bg-canvas px-3 py-2.5 text-xs"
    >
      <div
        data-testid="pool-estimate-status"
        className={`flex items-center gap-2 font-bold ${statusTone}`}
      >
        {isEmpty ? (
          <XCircleIcon className="h-4 w-4 flex-shrink-0" />
        ) : isInsufficient ? (
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
        ) : (
          <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
        )}
        <span>
          {isEmpty ? (
            "No URLs found in the last 24h with current filters"
          ) : (
            <>
              ~{compact(poolEstimate.total)} topic-eligible URLs
              {hasMarketContext &&
                ` of ${compact(poolEstimate.marketTotal)} total in pool`}
              {isInsufficient && ` (${grouped(urlsPerRun)} requested)`}
            </>
          )}
        </span>
      </div>

      {recommendedUrls !== null && (
        <div className="flex items-center gap-2 font-semibold text-ink-muted">
          <span>
            Recommended: ~{grouped(recommendedUrls)} URLs per run
          </span>
          <button
            type="button"
            onClick={() => onApplyRecommendation(recommendedUrls)}
            className="rounded-full border border-accent-active px-2.5 py-0.5 text-xs font-extrabold text-accent-active transition-opacity hover:opacity-80"
          >
            Apply
          </button>
        </div>
      )}

      {!isEmpty && hasRequests && (
        <div className="leading-relaxed text-ink-muted">
          <span className="font-semibold">Daily requests: </span>
          {compact(poolEstimate.totalRequests)} topic-eligible
          {poolEstimate.marketTotalRequests > poolEstimate.totalRequests &&
            `, ${compact(poolEstimate.marketTotalRequests)} total`}
        </div>
      )}

      {countryEntries.length > 0 && (
        <div className="leading-relaxed text-ink-muted">
          {countryEntries
            .map(([code, row]) => {
              const codeUpper = code.toUpperCase();
              const urls = compact(row.urls);
              const market =
                row.marketUrls > row.urls
                  ? `/${compact(row.marketUrls)}`
                  : "";
              const requests =
                row.requests > 0
                  ? ` (${compact(row.requests)} req)`
                  : "";
              return `${codeUpper}: ${urls}${market}${requests}`;
            })
            .join(" · ")}
        </div>
      )}

      {hasNoCoverage && (
        <div
          data-testid="pool-estimate-coverage"
          className="flex items-start gap-1.5 font-semibold text-warn"
        >
          <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {deadCountries.length > 0
              ? `No inventory for: ${deadCountries.join(", ")}. These countries will return 0 URLs per run.`
              : "One or more selected countries have no inventory. Those countries will return 0 URLs per run."}
          </span>
        </div>
      )}
    </div>
  );
};

export default PoolEstimatePanel;
